import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard, { catName } from '@/components/base/ArticleCard';
import Breadcrumbs from '@/components/base/Breadcrumbs';
import { articles, categories, siteName } from '@/mocks/articles';
import usePageMeta from '@/hooks/usePageMeta';
import JsonLd from '@/components/base/JsonLd';

const TABS = [{ slug: '', name: '全部' }, ...categories.map((c) => ({ slug: c.slug, name: c.name }))];

export default function NewsList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get('cat') ?? '';
  const q = searchParams.get('search') ?? '';

  const curCat = categories.find((c) => c.slug === cat);
  const catTitle = curCat ? `${curCat.name} - 最新爆料` : '最新数码爆料';

  const results = useMemo(() => {
    let list = articles;
    if (cat) list = list.filter((a) => a.category === cat);
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      list = list.filter((a) => [a.title, a.summary, ...a.tags].join(' ').toLowerCase().includes(kw));
    }
    return list;
  }, [cat, q]);

  usePageMeta(
    curCat ? `${curCat.name}栏目` : '最新数码爆料',
    `${siteName} ${catTitle}：${curCat?.brief ?? '第一时间汇总手机参数曝光、未发布产品图、配置价格预测与厂商内部消息。'}`
  );

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '最新爆料', to: '/news' },
    ...(curCat ? [{ label: curCat.name }] : []),
  ];

  return (
    <>
      <Navbar />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${catTitle} | ${siteName}`,
          inLanguage: 'zh-CN',
          mainEntity: {
            '@type': 'ItemList',
            numberOfItems: results.length,
            itemListElement: results.map((a, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: a.title,
              url: `https://agirx.cn/news/${a.id}`,
            })),
          },
        }}
      />
      <main id="main" className="bg-background-50">
        <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={breadcrumbItems} />
          <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                {catTitle}
              </h1>
              <p className="mt-2 text-sm text-foreground-600 md:text-base">
                {q ? `“${q}” 的搜索结果 · ` : ''}共 {results.length} 篇，每日滚动更新。
              </p>
            </div>
            <div className="flex w-full items-center gap-2 md:w-72">
              <div className="relative w-full">
                <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" aria-hidden="true" />
                <input
                  type="search"
                  value={q}
                  onChange={(e) => {
                    const val = e.target.value;
                    const next = new URLSearchParams(searchParams);
                    if (val) next.set('search', val);
                    else next.delete('search');
                    setSearchParams(next, { replace: true });
                  }}
                  placeholder="搜索爆料关键词…"
                  className="h-11 w-full rounded-full border border-background-200 bg-background-100 pl-10 pr-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-200"
                />
              </div>
            </div>
          </div>

          {/* 分类切换 */}
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {TABS.map((t) => {
              const active = cat === t.slug;
              return (
                <Link
                  key={t.slug || 'all'}
                  to={t.slug ? `/news?cat=${t.slug}` : '/news'}
                  aria-current={active ? 'page' : undefined}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                    active
                      ? 'bg-primary-500 text-background-50'
                      : 'border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-200 hover:text-primary-700'
                  }`}
                >
                  {t.name}
                </Link>
              );
            })}
          </div>

          {results.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((a) => (
                <ArticleCard key={a.id} article={a} />
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 py-20 text-center">
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-inbox-2-line text-2xl" aria-hidden="true" />
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-800">暂时没有匹配的爆料</p>
              <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看其他栏目。</p>
              <button
                type="button"
                onClick={() => setSearchParams({}, { replace: true })}
                className="mt-5 inline-flex h-10 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-refresh-line" aria-hidden="true" />
                清除筛选
              </button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}